import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import BulkActionBar from "../../components/BulkActionBar";
import EvidenceReferenceField from "../../components/EvidenceReferenceField";
import FixedIsoClauseField from "../../components/FixedIsoClauseField";
import {
  canRequestApprovalForItem as canRequestApprovalForRecord,
  isApprovalApproved,
  isApprovalPending,
  isApprovalPendingReview,
  requestBulkEmsApproval,
} from "../../lib/emsApproval";
import "../../styles/DashboardHome.css";
import "../../styles/Tables.css";
import "../../styles/LegalCompliance.css";
import {
  createLegalRegisterItemApi,
  decideEmsApprovalApi,
  deleteLegalRegisterItemApi,
  downloadLegalRegisterCsvApi,
  fetchEmsApprovalPolicyApi,
  fetchLegalRegisterApi,
  requestEmsApprovalApi,
  updateLegalRegisterItemApi,
  verifyEmsClosureApi,
  canApproveEmsModule,
} from "../../lib/api";

const MODULE_KEY = "legal_compliance";

const EMPTY_FORM = {
  iso_clause: "",
  requirement: "",
  source: "",
  applies_to: "",
  status: "compliant",
  owner: "",
  review_date: "",
  evidence: "",
  notes: "",
};

export default function LegalCompliance() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [exporting, setExporting] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterOwner, setFilterOwner] = useState("all");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [dueThisMonthOnly, setDueThisMonthOnly] = useState(false);
  const [isoClauseLabels, setIsoClauseLabels] = useState({});
  const [defaultIsoClause, setDefaultIsoClause] = useState("");
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [requestRoles, setRequestRoles] = useState([]);
  const [approveRoles, setApproveRoles] = useState([]);
  const [reviewRoles, setReviewRoles] = useState([]);
  const [verifyRoles, setVerifyRoles] = useState([]);
  const [workflowMode, setWorkflowMode] = useState("one_step");
  const [closureVerificationRequired, setClosureVerificationRequired] = useState(false);
  const [approvalBusyId, setApprovalBusyId] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkRequesting, setBulkRequesting] = useState(false);

  const loadItems = async () => {
    try {
      setLoading(true);
      const data = await fetchLegalRegisterApi();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.message || "Failed to load legal register");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPolicy = async () => {
    try {
      const payload = await fetchEmsApprovalPolicyApi();
      const policyRows = Array.isArray(payload?.policy) ? payload.policy : [];
      const labels = payload?.iso_clause_labels && typeof payload.iso_clause_labels === "object"
        ? payload.iso_clause_labels
        : {};
      const modulePolicy = policyRows.find((r) => String(r?.module_key || "") === MODULE_KEY) || {};
      const options = Array.isArray(modulePolicy?.iso_clause_options)
        ? modulePolicy.iso_clause_options.map((x) => String(x))
        : [];
      const fallbackDefault = String(modulePolicy?.iso_clause_default || options[0] || "");
      const reqRoles = Array.isArray(modulePolicy?.request_roles) ? modulePolicy.request_roles.map((x) => String(x).toLowerCase()) : [];
      const appRoles = Array.isArray(modulePolicy?.approve_roles) ? modulePolicy.approve_roles.map((x) => String(x).toLowerCase()) : [];
      const revRoles = Array.isArray(modulePolicy?.review_roles) ? modulePolicy.review_roles.map((x) => String(x).toLowerCase()) : appRoles;
      const verRoles = Array.isArray(modulePolicy?.verify_roles) ? modulePolicy.verify_roles.map((x) => String(x).toLowerCase()) : appRoles;
      const wfMode = String(modulePolicy?.approval_workflow_mode || "one_step").toLowerCase();
      setIsoClauseLabels(labels);
      setDefaultIsoClause(fallbackDefault);
      setRequestRoles(reqRoles);
      setApproveRoles(appRoles);
      setReviewRoles(revRoles);
      setVerifyRoles(verRoles);
      setWorkflowMode(wfMode === "two_step" ? "two_step" : "one_step");
      setClosureVerificationRequired(Boolean(modulePolicy?.closure_verification_required));
      setForm((prev) => ({ ...prev, iso_clause: prev.iso_clause || fallbackDefault }));
    } catch {
      setIsoClauseLabels({});
      setDefaultIsoClause("6");
      setRequestRoles(["client", "admin"]);
      setApproveRoles(["admin"]);
      setReviewRoles(["admin"]);
      setVerifyRoles(["admin"]);
      setWorkflowMode("one_step");
      setClosureVerificationRequired(false);
      setForm((prev) => ({ ...prev, iso_clause: prev.iso_clause || "6" }));
    }
  };

  useEffect(() => {
    loadItems();
    loadPolicy();
  }, []);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const resetForm = () => {
    setForm({ ...EMPTY_FORM, iso_clause: defaultIsoClause });
    setEditingId("");
    setSubmitAttempted(false);
  };

  const validationErrors = useMemo(() => {
    const errs = [];
    if (!String(form.requirement || "").trim()) errs.push("Requirement is required.");
    if (!String(form.iso_clause || "").trim()) errs.push("ISO clause is required.");
    const st = String(form.status || "").trim().toLowerCase();
    if (st === "compliant" || st === "not_applicable") {
      if (!String(form.source || "").trim()) errs.push("Source is required before setting this completion status.");
      if (!String(form.applies_to || "").trim()) errs.push("Applies to is required before setting this completion status.");
      if (!String(form.owner || "").trim()) errs.push("Owner is required before setting this completion status.");
      if (!String(form.review_date || "").trim()) errs.push("Review date is required before setting this completion status.");
      if (!String(form.evidence || "").trim()) errs.push("Evidence is required before setting this completion status.");
    }
    return errs;
  }, [form]);

  const statusBadgeClass = (status) => `badge badge-status-${String(status || "").trim().toLowerCase().replace(/\s+/g, "_")}`;
  const approvalBadgeClass = (status) => `badge badge-approval-${String(status || "not_requested").trim().toLowerCase().replace(/\s+/g, "_")}`;
  const userRole = String(localStorage.getItem("role") || "").trim().toLowerCase();
  const canRequest = requestRoles.includes(userRole);
  const canApprove = approveRoles.includes(userRole) && canApproveEmsModule("legal_compliance");
  const canReview = reviewRoles.includes(userRole);
  const canVerify = verifyRoles.includes(userRole);
  const completionStatuses = useMemo(() => ["compliant", "not_applicable"], []);
  const canRequestApprovalForItem = useCallback((item) =>
    canRequestApprovalForRecord(item, canRequest, completionStatuses),
    [canRequest, completionStatuses]
  );
  const isCompletionStatus = (status) => completionStatuses.includes(String(status || "").trim().toLowerCase());

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      iso_clause: item.iso_clause || defaultIsoClause,
      requirement: item.requirement || "",
      source: item.source || "",
      applies_to: item.applies_to || "",
      status: item.status || "compliant",
      owner: item.owner || "",
      review_date: item.review_date || "",
      evidence: item.evidence || "",
      notes: item.notes || "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    if (validationErrors.length) {
      toast.error(validationErrors[0]);
      return;
    }

    try {
      setSaving(true);
      if (editingId) {
        await updateLegalRegisterItemApi(editingId, form);
        toast.success("Legal entry updated");
      } else {
        await createLegalRegisterItemApi(form);
        toast.success("Legal entry created");
      }
      resetForm();
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to save legal entry");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const ok = window.confirm("Delete this legal register entry?");
    if (!ok) return;
    try {
      setDeletingId(id);
      await deleteLegalRegisterItemApi(id);
      toast.success("Legal entry deleted");
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to delete legal entry");
    } finally {
      setDeletingId("");
    }
  };

  const handleRequestApproval = async (item) => {
    try {
      setApprovalBusyId(item.id);
      const notes = window.prompt("Optional approval request note:", "") || "";
      await requestEmsApprovalApi({
        module_key: MODULE_KEY,
        item_id: item.id,
        notes,
      });
      toast.success("Approval requested");
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to request approval");
    } finally {
      setApprovalBusyId("");
    }
  };

  const handleDecision = async (item, decision) => {
    try {
      setApprovalBusyId(item.id);
      const notes = window.prompt(`Optional note for ${decision}:`, "") || "";
      await decideEmsApprovalApi({
        module_key: MODULE_KEY,
        item_id: item.id,
        decision,
        notes,
      });
      const msg = decision === "review" ? "Item reviewed" : decision === "approve" ? "Item approved" : "Item rejected";
      toast.success(msg);
      await loadItems();
    } catch (err) {
      toast.error(err?.message || `Failed to ${decision}`);
    } finally {
      setApprovalBusyId("");
    }
  };

  const handleVerifyClose = async (item) => {
    try {
      setApprovalBusyId(item.id);
      const notes = window.prompt("Optional closure verification note:", "") || "";
      await verifyEmsClosureApi({
        module_key: MODULE_KEY,
        item_id: item.id,
        notes,
      });
      toast.success("Closure verified");
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to verify closure");
    } finally {
      setApprovalBusyId("");
    }
  };

  const handleExportCsv = async () => {
    try {
      setExporting(true);
      const blob = await downloadLegalRegisterCsvApi();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "legal_register.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      toast.success("Legal register exported");
    } catch (err) {
      toast.error(err?.message || "Failed to export legal register");
    } finally {
      setExporting(false);
    }
  };

  const ownerOptions = useMemo(() => {
    const set = new Set(
      (items || [])
        .map((it) => String(it.owner || "").trim())
        .filter(Boolean)
    );
    return Array.from(set).sort();
  }, [items]);

  const filteredItems = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const ym = new Date().toISOString().slice(0, 7);
    return (items || []).filter((it) => {
      if (filterStatus !== "all" && String(it.status || "") !== filterStatus) return false;
      if (filterOwner === "__unassigned__") {
        if (String(it.owner || "").trim()) return false;
      } else if (filterOwner !== "all" && String(it.owner || "") !== filterOwner) {
        return false;
      }
      if (overdueOnly) {
        const rd = String(it.review_date || "").trim();
        if (!rd || rd >= today) return false;
      }
      if (dueThisMonthOnly) {
        const rd = String(it.review_date || "").trim();
        if (!rd.startsWith(ym)) return false;
      }
      return true;
    });
  }, [items, filterStatus, filterOwner, overdueOnly, dueThisMonthOnly]);

  const selectableFilteredIds = useMemo(
    () => filteredItems.filter((it) => canRequestApprovalForItem(it)).map((it) => String(it.id)),
    [filteredItems, canRequestApprovalForItem]
  );
  const selectedSet = useMemo(() => new Set(selectedIds.map((x) => String(x))), [selectedIds]);
  const allSelectableSelected = selectableFilteredIds.length > 0 && selectableFilteredIds.every((id) => selectedSet.has(String(id)));
  const selectedEligibleItems = useMemo(
    () => filteredItems.filter((it) => selectedSet.has(String(it.id)) && canRequestApprovalForItem(it)),
    [filteredItems, selectedSet, canRequestApprovalForItem]
  );

  useEffect(() => {
    const liveIds = new Set((items || []).map((it) => String(it.id)));
    setSelectedIds((prev) => prev.filter((id) => liveIds.has(String(id))));
  }, [items]);

  const applyPreset = (preset) => {
    if (preset === "non_compliant") {
      setFilterStatus("non_compliant");
      setFilterOwner("all");
      setOverdueOnly(false);
      setDueThisMonthOnly(false);
      return;
    }
    if (preset === "due_this_month") {
      setFilterStatus("all");
      setFilterOwner("all");
      setOverdueOnly(false);
      setDueThisMonthOnly(true);
      return;
    }
    if (preset === "unassigned_owner") {
      setFilterStatus("all");
      setFilterOwner("__unassigned__");
      setOverdueOnly(false);
      setDueThisMonthOnly(false);
      return;
    }
    setFilterStatus("all");
    setFilterOwner("all");
    setOverdueOnly(false);
    setDueThisMonthOnly(false);
  };

  const toggleSelectOne = (itemId, checked) => {
    const id = String(itemId);
    setSelectedIds((prev) => {
      const next = new Set(prev.map((x) => String(x)));
      if (checked) next.add(id);
      else next.delete(id);
      return Array.from(next);
    });
  };

  const toggleSelectAllFiltered = (checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev.map((x) => String(x)));
      for (const id of selectableFilteredIds) {
        if (checked) next.add(String(id));
        else next.delete(String(id));
      }
      return Array.from(next);
    });
  };

  const handleBulkRequestApproval = async () => {
    if (!selectedEligibleItems.length) {
      toast.error("Select at least one eligible row");
      return;
    }
    const notes = window.prompt("Optional bulk approval request note:", "") || "";
    try {
      setBulkRequesting(true);
      const { ok, failed } = await requestBulkEmsApproval(
        requestEmsApprovalApi,
        MODULE_KEY,
        selectedEligibleItems,
        notes
      );
      if (ok > 0) toast.success(`Requested approval for ${ok} item(s)`);
      if (failed > 0) toast.error(`${failed} item(s) failed`);
      setSelectedIds([]);
      await loadItems();
    } finally {
      setBulkRequesting(false);
    }
  };

  return (
    <div className="dashboard-page legal-compliance-page">
      <div className="dashboard-header">
        <div>
          <p className="legal-compliance-eyebrow">ISO 14001 planning</p>
          <h1>Legal Compliance Register</h1>
          <p className="subtitle">Record confirmed environmental obligations, who they apply to, how they are checked, and the evidence that demonstrates compliance.</p>
        </div>
      </div>

      <section className="card legal-compliance-guidance">
        <div>
          <p className="legal-compliance-eyebrow">How to complete an entry</p>
          <h3>Record the obligation, then show how it is managed</h3>
          <p>Use this register for requirements that have been confirmed as relevant to your organisation. It supports compliance management but does not replace legal advice.</p>
        </div>
        <div className="legal-compliance-guidance-grid">
          <div><strong>1. Confirm the source</strong><span>Use legislation, permits, licences, regulator guidance, contracts or another competent source.</span></div>
          <div><strong>2. Define applicability</strong><span>State the site, activity, equipment or team the obligation applies to.</span></div>
          <div><strong>3. Keep evidence</strong><span>Link the records that show the obligation is being met and reviewed.</span></div>
        </div>
      </section>

      <div className="card settings-card legal-compliance-form">
        <div className="settings-header">
          <div>
            <p className="legal-compliance-eyebrow">Your compliance entry</p>
            <h3>{editingId ? "Edit Legal Entry" : "Add Legal Entry"}</h3>
          </div>
          <span className="kpi-sub">Record a confirmed obligation, who it applies to, and the evidence that supports the selected status.</span>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="settings-grid">
            <FixedIsoClauseField clause={defaultIsoClause} labels={isoClauseLabels} />
            <label className="settings-field">
              <span>Requirement</span>
              <input placeholder="e.g. Maintain waste transfer notes for waste collections" value={form.requirement} onChange={(e) => setField("requirement", e.target.value)} disabled={saving} />
              <small>Describe the action or outcome your organisation must achieve, not just the name of the law.</small>
            </label>
            <label className="settings-field">
              <span>Source</span>
              <input placeholder="e.g. Waste duty of care legislation and regulator guidance" value={form.source} onChange={(e) => setField("source", e.target.value)} disabled={saving} />
              <small>Reference the legislation, permit, licence, regulator guidance, contract, or competent advice used to confirm the requirement.</small>
            </label>
            <label className="settings-field">
              <span>Applies to</span>
              <input placeholder="e.g. UK warehouse, facilities team and waste contractor" value={form.applies_to} onChange={(e) => setField("applies_to", e.target.value)} disabled={saving} />
              <small>State the site, activity, equipment, process, role or contractor affected by the requirement.</small>
            </label>
            <label className="settings-field">
              <span>Status</span>
              <select value={form.status} onChange={(e) => setField("status", e.target.value)} disabled={saving}>
                <option value="compliant">compliant</option>
                <option value="partially_compliant">partially_compliant</option>
                <option value="non_compliant">non_compliant</option>
                <option value="not_applicable">not_applicable</option>
              </select>
              <small>Select the status supported by current evidence. Use partially compliant or non-compliant where an action is still required.</small>
            </label>
            <label className="settings-field">
              <span>Owner</span>
              <input placeholder="e.g. Operations Manager" value={form.owner} onChange={(e) => setField("owner", e.target.value)} disabled={saving} />
              <small>Name the person or role responsible for monitoring this obligation and completing any actions.</small>
            </label>
            <label className="settings-field">
              <span>Review date</span>
              <input type="date" value={form.review_date} onChange={(e) => setField("review_date", e.target.value)} disabled={saving} />
              <small>Set the next check date, and bring it forward when legislation, permits or operations change.</small>
            </label>
            <EvidenceReferenceField
              label="Evidence reference"
              value={form.evidence}
              onChange={(value) => setField("evidence", value)}
              disabled={saving}
              helpText="Link permits, licences, inspection records, waste transfer notes, monitoring logs, training records or other evidence supporting the selected status."
            />
            <label className="settings-field legal-compliance-wide-field">
              <span>Notes</span>
              <input placeholder="Optional: record advice received, open actions, changes to operations or reasons for the selected status." value={form.notes} onChange={(e) => setField("notes", e.target.value)} disabled={saving} />
              <small>Use notes to preserve the context needed for the next review or audit.</small>
            </label>
          </div>
          {submitAttempted && validationErrors.length > 0 ? (
            <div className="form-error-summary">
              Please fix the following before saving:
              <ul>
                {validationErrors.map((msg) => <li key={msg}>{msg}</li>)}
              </ul>
            </div>
          ) : null}
          <div className="settings-actions">
            {editingId ? (
              <button type="button" className="btn-secondary" onClick={resetForm} disabled={saving} style={{ marginRight: 10 }}>
                Cancel
              </button>
            ) : null}
            <button className="btn-primary" type="submit" disabled={saving}>
              {saving ? "Saving..." : editingId ? "Update legal entry" : "Add legal entry"}
            </button>
          </div>
        </form>
      </div>

      <BulkActionBar
        selectedCount={selectedIds.length}
        eligibleCount={selectedEligibleItems.length}
        onClear={() => setSelectedIds([])}
        onRequest={handleBulkRequestApproval}
        requesting={bulkRequesting}
        requestDisabled={bulkRequesting || selectedEligibleItems.length === 0}
        onExport={handleExportCsv}
        exporting={exporting}
      />

      <div className="filter-bar">
        <label className="settings-field">
          <span>Status</span>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">all</option>
            <option value="compliant">compliant</option>
            <option value="partially_compliant">partially_compliant</option>
            <option value="non_compliant">non_compliant</option>
            <option value="not_applicable">not_applicable</option>
          </select>
        </label>

        <label className="settings-field">
          <span>Owner</span>
          <select value={filterOwner} onChange={(e) => setFilterOwner(e.target.value)}>
            <option value="all">all</option>
            <option value="__unassigned__">unassigned</option>
            {ownerOptions.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </label>

        <label className="checkbox-field">
          <input type="checkbox" checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} />
          Overdue review only
        </label>

        <span className="filter-count">{filteredItems.length} of {items.length} shown</span>
      </div>

      <div className="preset-bar">
        <button className="btn-secondary btn-small" onClick={() => applyPreset("non_compliant")}>Only non-compliant</button>
        <button className="btn-secondary btn-small" onClick={() => applyPreset("due_this_month")}>Review due this month</button>
        <button className="btn-secondary btn-small" onClick={() => applyPreset("unassigned_owner")}>Unassigned owner</button>
        <button className="btn-secondary btn-small" onClick={() => applyPreset("clear")}>Clear presets</button>
      </div>

      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={allSelectableSelected}
                  onChange={(e) => toggleSelectAllFiltered(e.target.checked)}
                  disabled={!selectableFilteredIds.length}
                />
              </th>
              <th>ISO Clause</th>
              <th>Requirement</th>
              <th>Status</th>
              <th>Approval</th>
              <th>Owner</th>
              <th>Review date</th>
              <th>Evidence</th>
              <th>Updated by</th>
              <th>Updated at</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} className="table-empty">Loading...</td></tr>
            ) : filteredItems.length === 0 ? (
              <tr><td colSpan={11} className="table-empty">No legal entries yet.</td></tr>
            ) : (
              filteredItems.map((it) => (
                <tr key={it.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedSet.has(String(it.id))}
                      onChange={(e) => toggleSelectOne(it.id, e.target.checked)}
                      disabled={!canRequestApprovalForItem(it)}
                    />
                  </td>
                  <td>{it.iso_clause ? (isoClauseLabels[it.iso_clause] ? `${it.iso_clause} - ${isoClauseLabels[it.iso_clause]}` : `Clause ${it.iso_clause}`) : "-"}</td>
                  <td>{it.requirement || "-"}</td>
                  <td><span className={statusBadgeClass(it.status)}>{it.status || "-"}</span></td>
                  <td><span className={approvalBadgeClass(it.approval_status)}>{it.approval_status || "not_requested"}</span></td>
                  <td>{it.owner || "-"}</td>
                  <td>{it.review_date || "-"}</td>
                  <td>{it.evidence || "-"}</td>
                  <td>{it.updated_by || "-"}</td>
                  <td>{it.updated_at || "-"}</td>
                  <td>
                    <button className="admin-btn secondary" onClick={() => startEdit(it)} style={{ marginRight: 8 }}>Edit</button>
                    {canRequestApprovalForItem(it) ? (
                      <button
                        className="admin-btn"
                        onClick={() => handleRequestApproval(it)}
                        disabled={approvalBusyId === it.id}
                        style={{ marginRight: 8 }}
                      >
                        {approvalBusyId === it.id ? "Working..." : "Request"}
                      </button>
                    ) : null}
                    {workflowMode === "two_step" && canReview && isApprovalPendingReview(it) ? (
                      <>
                        <button
                          className="admin-btn"
                          onClick={() => handleDecision(it, "review")}
                          disabled={approvalBusyId === it.id}
                          style={{ marginRight: 8 }}
                        >
                          {approvalBusyId === it.id ? "Working..." : "Review"}
                        </button>
                        <button
                          className="admin-btn secondary"
                          onClick={() => handleDecision(it, "reject")}
                          disabled={approvalBusyId === it.id}
                          style={{ marginRight: 8 }}
                        >
                          {approvalBusyId === it.id ? "Working..." : "Reject"}
                        </button>
                      </>
                    ) : null}
                    {canApprove && isApprovalPending(it) ? (
                      <>
                        <button
                          className="admin-btn"
                          onClick={() => handleDecision(it, "approve")}
                          disabled={approvalBusyId === it.id}
                          style={{ marginRight: 8 }}
                        >
                          {approvalBusyId === it.id ? "Working..." : "Approve"}
                        </button>
                        <button
                          className="admin-btn secondary"
                          onClick={() => handleDecision(it, "reject")}
                          disabled={approvalBusyId === it.id}
                          style={{ marginRight: 8 }}
                        >
                          {approvalBusyId === it.id ? "Working..." : "Reject"}
                        </button>
                      </>
                    ) : null}
                    {closureVerificationRequired
                    && canVerify
                    && isCompletionStatus(it.status)
                    && isApprovalApproved(it)
                    && !Boolean(it.closure_verified) ? (
                      <button
                        className="admin-btn"
                        onClick={() => handleVerifyClose(it)}
                        disabled={approvalBusyId === it.id}
                        style={{ marginRight: 8 }}
                      >
                        {approvalBusyId === it.id ? "Working..." : "Verify close"}
                      </button>
                    ) : null}
                    <button className="admin-btn danger" onClick={() => handleDelete(it.id)} disabled={deletingId === it.id}>
                      {deletingId === it.id ? "Deleting..." : "Delete"}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}



